import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Search, Receipt as ReceiptIcon, Ban, ArrowLeft, Wallet, Zap, Layers } from "lucide-react";
import { toast } from "sonner";

import { formatDate, formatDateTime } from "@/lib/datetime";
const MODES = ["cash", "bank", "upi", "cheque", "other"];
const MODE_TONE = {
  cash: "bg-emerald-50 text-emerald-800 border-emerald-200",
  bank: "bg-blue-50 text-blue-800 border-blue-200",
  upi: "bg-purple-50 text-purple-800 border-purple-200",
  cheque: "bg-amber-50 text-amber-800 border-amber-200",
  other: "bg-gray-100 text-gray-700 border-gray-200",
};

export default function Receipts() {
  const [mode, setMode] = useState("list"); // list | new
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [payerTypeFilter, setPayerTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [advanceTarget, setAdvanceTarget] = useState(null); // receipt object being applied

  const load = useCallback(async () => {
    try {
      const params = { page, page_size: 50 };
      if (statusFilter) params.status = statusFilter;
      if (payerTypeFilter) params.payer_type = payerTypeFilter;
      const r = await api.get("/sales/receipts", { params });
      let list = r.data.items || [];
      if (search) {
        const s = search.toLowerCase();
        list = list.filter((x) => (x.receipt_no || "").toLowerCase().includes(s) || (x.payer_snapshot?.name || "").toLowerCase().includes(s) || (x.reference || "").toLowerCase().includes(s));
      }
      setItems(list);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [page, statusFilter, payerTypeFilter, search]);
  useEffect(() => { if (mode === "list") load(); }, [load, mode]);

  const cancel = async (r) => {
    if (!window.confirm(`Cancel receipt ${r.receipt_no}? The allocated invoices will be re-opened.`)) return;
    try { await api.post(`/sales/receipts/${r.id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  if (mode === "new") return <ReceiptForm onBack={() => { setMode("list"); load(); }} />;

  return (
    <div className="space-y-4">
      <PageSection
        title="Receipts"
        description="Money received from customers and franchises. Auto-allocated FIFO against oldest unpaid invoices unless manually chosen."
        action={<Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={() => setMode("new")} data-testid="rcp-new-btn"><Plus className="h-4 w-4" /> New Receipt</Button>}
      >
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 max-w-xs min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Receipt # / payer / reference…" className="pl-8" data-testid="rcp-search" />
            </div>
            <select value={payerTypeFilter} onChange={(e) => setPayerTypeFilter(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="rcp-payer-filter">
              <option value="">All payers</option>
              <option value="customer">Customer</option>
              <option value="franchise">Franchise</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="rcp-status-filter">
              <option value="">All status</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <span className="ml-auto text-xs text-gray-500">{total.toLocaleString()} receipt(s)</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Receipt #</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Payer</th>
                <th className="px-3 py-2">Mode</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-right">Allocated</th>
                <th className="px-3 py-2 text-right">Unallocated</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-10 text-center text-gray-400"><ReceiptIcon className="h-8 w-8 mx-auto mb-2 opacity-30" /> No receipts yet.</td></tr>
              )}
              {items.map((r) => (
                <tr key={r.id} className={`hover:bg-gray-50 ${r.status === "cancelled" ? "opacity-60" : ""}`} data-testid={`rcp-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold">{r.receipt_no}</td>
                  <td className="px-3 py-2 text-xs">{formatDate(r.receipt_date)}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.payer_snapshot?.name || "—"}</div>
                    <div className="text-[11px] text-gray-500">{r.payer_type} · {r.payer_snapshot?.code || ""}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${MODE_TONE[r.mode] || ""}`}>{r.mode}</span>
                    {r.reference && <div className="text-[10px] text-gray-500 font-mono mt-0.5">{r.reference}</div>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatINR(r.amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(r.allocated_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{r.unallocated_amount > 0 ? <span className="text-amber-700 font-semibold">{formatINR(r.unallocated_amount)}</span> : formatINR(0)}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-widest font-semibold ${r.status === "confirmed" ? "text-emerald-700" : "text-red-700"}`}>{r.status}</span></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.status === "confirmed" && r.unallocated_amount > 0.005 && (
                      <button className="h-7 px-2 rounded hover:bg-amber-50 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 border border-amber-200 mr-1" onClick={() => setAdvanceTarget(r)} title="Apply advance to open invoices" data-testid={`rcp-apply-${r.id}`}>
                        <Layers className="h-3 w-3" /> Apply
                      </button>
                    )}
                    {r.status === "confirmed" && (
                      <button className="h-7 w-7 rounded hover:bg-red-50 inline-flex items-center justify-center" onClick={() => cancel(r)} title="Cancel receipt" data-testid={`rcp-cancel-${r.id}`}>
                        <Ban className="h-3.5 w-3.5 text-red-500" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      </PageSection>
      {advanceTarget && (
        <ApplyAdvanceModal receipt={advanceTarget} onClose={() => setAdvanceTarget(null)} onSaved={() => { setAdvanceTarget(null); load(); }} />
      )}
    </div>
  );
}

// -------- Receipt Form ------------------------------------------------------
function ReceiptForm({ onBack }) {
  const [customers, setCustomers] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [payerType, setPayerType] = useState("customer");
  const [payerId, setPayerId] = useState("");
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState("cash");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [autoAllocate, setAutoAllocate] = useState(true);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [manualAllocs, setManualAllocs] = useState({}); // invoice_id → amount
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/sales/customers", { params: { page: 1, page_size: 500 } }).then((r) => setCustomers(r.data?.items || []));
    api.get("/sales/branches").then((r) => setFranchises((r.data || []).filter((b) => b.branch_type === "franchise")));
  }, []);

  useEffect(() => {
    if (!payerId) { setOpenInvoices([]); return; }
    api.get("/sales/receipts/open-invoices", { params: { payer_type: payerType, payer_id: payerId } })
      .then((r) => setOpenInvoices(r.data || []))
      .catch(() => setOpenInvoices([]));
    setManualAllocs({});
  }, [payerType, payerId]);

  const totalManual = useMemo(() => Object.values(manualAllocs).reduce((s, v) => s + (Number(v) || 0), 0), [manualAllocs]);
  const amountNum = Number(amount) || 0;
  const overAllocated = totalManual > amountNum + 0.005;

  const fifoPreview = useMemo(() => {
    if (autoAllocate) {
      let rem = amountNum;
      return openInvoices.map((inv) => {
        const apply = Math.min(rem, inv.outstanding);
        rem -= Math.max(0, apply);
        return { ...inv, _apply: apply > 0.005 ? apply : 0 };
      }).filter((i) => i._apply > 0);
    }
    return null;
  }, [autoAllocate, amountNum, openInvoices]);

  const save = async () => {
    if (!payerId) return toast.error("Pick a payer");
    if (amountNum <= 0) return toast.error("Amount must be greater than zero");
    if (!autoAllocate && overAllocated) return toast.error("Allocations exceed receipt amount");
    setSaving(true);
    try {
      const payload = {
        payer_type: payerType, payer_id: payerId, receipt_date: receiptDate,
        mode, amount: amountNum, reference: reference || null, notes: notes || null,
        auto_allocate: autoAllocate,
        allocations: autoAllocate ? [] : Object.entries(manualAllocs).filter(([, v]) => Number(v) > 0.005).map(([iid, v]) => ({ invoice_id: iid, amount: Number(v) })),
      };
      await api.post("/sales/receipts", payload);
      toast.success("Receipt saved");
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={onBack} className="gap-1.5" data-testid="rcp-back"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <h2 className="font-heading font-bold text-lg text-gray-900">New Receipt</h2>
        <Button className="ml-auto bg-emerald-700 hover:bg-emerald-800 gap-1.5" onClick={save} disabled={saving} data-testid="rcp-save"><Wallet className="h-4 w-4" /> {saving ? "Saving…" : "Save Receipt"}</Button>
      </div>

      <Surface>
        <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100">Payer & Amount</h3>
        <div className="grid grid-cols-4 gap-4">
          <F label="Payer Type">
            <div className="flex gap-2">
              {["customer", "franchise"].map((t) => (
                <button key={t} onClick={() => { setPayerType(t); setPayerId(""); }} className={`flex-1 h-9 rounded-md border text-sm font-semibold capitalize ${payerType === t ? "border-[#9B1C1C] bg-[#9B1C1C]/5 text-[#9B1C1C]" : "border-gray-200 hover:bg-gray-50"}`} data-testid={`rcp-payer-type-${t}`}>{t}</button>
              ))}
            </div>
          </F>
          <F label={`${payerType === "customer" ? "Customer" : "Franchise"} *`} className="col-span-2">
            <select value={payerId} onChange={(e) => setPayerId(e.target.value)} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="rcp-payer">
              <option value="">— Select —</option>
              {(payerType === "customer" ? customers : franchises).map((x) => (
                <option key={x.id} value={x.id}>{x.code} · {x.name}</option>
              ))}
            </select>
          </F>
          <F label="Receipt Date *"><Input type="date" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} data-testid="rcp-date" /></F>
          <F label="Mode">
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="rcp-mode">
              {MODES.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
            </select>
          </F>
          <F label="Amount ₹ *"><Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="rcp-amount" /></F>
          <F label="Reference (chq#/txn#)"><Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="rcp-ref" /></F>
          <F label="Notes"><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="rcp-notes" /></F>
        </div>
      </Surface>

      <Surface padded={false}>
        <div className="p-3 border-b border-gray-100 flex items-center gap-3">
          <h3 className="font-heading font-semibold text-sm">Allocation</h3>
          <div className="flex gap-2 ml-auto">
            {[[true, "Auto (FIFO)"], [false, "Manual"]].map(([v, l]) => (
              <button key={String(v)} onClick={() => setAutoAllocate(v)} className={`h-8 px-3 rounded-md border text-xs font-semibold ${autoAllocate === v ? "border-[#9B1C1C] bg-[#9B1C1C]/5 text-[#9B1C1C]" : "border-gray-200"}`} data-testid={`rcp-alloc-${v ? "auto" : "manual"}`}>
                {v && <Zap className="inline h-3 w-3 mr-1 -mt-0.5" />}
                {l}
              </button>
            ))}
          </div>
        </div>
        {payerId ? (
          openInvoices.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 text-sm">
              No unpaid invoices — the entire receipt amount will be stored as advance ({formatINR(amountNum)}).
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                  <th className="px-3 py-2">Invoice #</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2 text-right">Grand Total</th>
                  <th className="px-3 py-2 text-right">Already Paid</th>
                  <th className="px-3 py-2 text-right">Outstanding</th>
                  <th className="px-3 py-2 text-right">Apply</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {openInvoices.map((inv) => {
                  const preview = fifoPreview?.find((x) => x.id === inv.id);
                  return (
                    <tr key={inv.id} data-testid={`rcp-inv-${inv.id}`}>
                      <td className="px-3 py-2 font-mono text-xs font-semibold">{inv.invoice_no}</td>
                      <td className="px-3 py-2 text-xs">{formatDate(inv.invoice_date)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(inv.grand_total)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-500">{formatINR(inv.allocated)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-700">{formatINR(inv.outstanding)}</td>
                      <td className="px-3 py-2 text-right">
                        {autoAllocate ? (
                          <span className="tabular-nums text-emerald-700 font-semibold text-sm" data-testid={`rcp-inv-fifo-${inv.id}`}>{preview ? formatINR(preview._apply) : "—"}</span>
                        ) : (
                          <Input type="number" step="0.01" min="0" max={inv.outstanding} value={manualAllocs[inv.id] || ""}
                            onChange={(e) => setManualAllocs((s) => ({ ...s, [inv.id]: e.target.value }))}
                            className="h-8 w-24 text-right tabular-nums font-mono ml-auto"
                            data-testid={`rcp-inv-manual-${inv.id}`} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 text-xs">
                <tr>
                  <td colSpan={5} className="px-3 py-2 text-right font-semibold">Total Allocated</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">
                    {autoAllocate
                      ? formatINR(fifoPreview?.reduce((s, i) => s + i._apply, 0) || 0)
                      : <span className={overAllocated ? "text-red-600" : ""}>{formatINR(totalManual)}</span>}
                  </td>
                </tr>
                <tr>
                  <td colSpan={5} className="px-3 py-2 text-right font-semibold">Advance / Unallocated</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-amber-700">
                    {formatINR(Math.max(0, amountNum - (autoAllocate ? (fifoPreview?.reduce((s, i) => s + i._apply, 0) || 0) : totalManual)))}
                  </td>
                </tr>
              </tfoot>
            </table>
          )
        ) : (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">Pick a {payerType} first to see their unpaid invoices.</div>
        )}
      </Surface>
    </div>
  );
}

const F = ({ label, className = "", children }) => (
  <label className={`text-sm ${className}`}>
    <span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">{label}</span>
    {children}
  </label>
);

// -------- Apply Advance Modal ----------------------------------------------
function ApplyAdvanceModal({ receipt, onClose, onSaved }) {
  const [openInvs, setOpenInvs] = useState([]);
  const [autoAllocate, setAutoAllocate] = useState(true);
  const [manual, setManual] = useState({});
  const [saving, setSaving] = useState(false);
  const available = Number(receipt.unallocated_amount || 0);

  useEffect(() => {
    api.get("/sales/receipts/open-invoices", { params: { payer_type: receipt.payer_type, payer_id: receipt.payer_id } })
      .then((r) => setOpenInvs(r.data || []))
      .catch(() => setOpenInvs([]));
  }, [receipt.payer_type, receipt.payer_id]);

  const fifoPreview = useMemo(() => {
    if (!autoAllocate) return null;
    let rem = available;
    return openInvs.map((inv) => {
      const apply = Math.min(rem, inv.outstanding);
      rem -= Math.max(0, apply);
      return { ...inv, _apply: apply > 0.005 ? apply : 0 };
    }).filter((i) => i._apply > 0);
  }, [autoAllocate, available, openInvs]);

  const manualTotal = useMemo(() => Object.values(manual).reduce((s, v) => s + (Number(v) || 0), 0), [manual]);
  const overApplied = !autoAllocate && manualTotal > available + 0.005;
  const applyTotal = autoAllocate ? (fifoPreview?.reduce((s, i) => s + i._apply, 0) || 0) : manualTotal;

  const save = async () => {
    if (applyTotal <= 0.005) return toast.error("Nothing to apply");
    if (overApplied) return toast.error("Amount exceeds unallocated advance");
    setSaving(true);
    try {
      const payload = {
        auto_allocate: autoAllocate,
        allocations: autoAllocate ? [] : Object.entries(manual).filter(([, v]) => Number(v) > 0.005).map(([iid, v]) => ({ invoice_id: iid, amount: Number(v) })),
      };
      await api.post(`/sales/receipts/${receipt.id}/apply-advance`, payload);
      toast.success(`Applied ${formatINR(applyTotal)} to ${autoAllocate ? fifoPreview.length : Object.values(manual).filter((v) => Number(v) > 0.005).length} invoice(s)`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="rcp-apply-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center gap-3">
          <div>
            <h3 className="font-heading font-bold text-gray-900">Apply Advance</h3>
            <div className="text-xs text-gray-500 mt-0.5">Receipt <span className="font-mono font-semibold text-gray-700">{receipt.receipt_no}</span> · {receipt.payer_snapshot?.name}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Advance Available</div>
            <div className="font-heading font-bold text-lg text-amber-700 tabular-nums" data-testid="rcp-advance-available">{formatINR(available)}</div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Allocation:</span>
            {[[true, "Auto (FIFO)"], [false, "Manual"]].map(([v, l]) => (
              <button key={String(v)} onClick={() => setAutoAllocate(v)} className={`h-8 px-3 rounded-md border text-xs font-semibold ${autoAllocate === v ? "border-[#9B1C1C] bg-[#9B1C1C]/5 text-[#9B1C1C]" : "border-gray-200"}`} data-testid={`rcp-advance-mode-${v ? "auto" : "manual"}`}>
                {v && <Zap className="inline h-3 w-3 mr-1 -mt-0.5" />}{l}
              </button>
            ))}
          </div>

          {openInvs.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-10 border border-dashed border-gray-200 rounded-md">
              No open invoices for this payer. The advance will stay on the receipt.
            </div>
          ) : (
            <table className="w-full text-sm border border-gray-200 rounded-md overflow-hidden">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                  <th className="px-3 py-2">Invoice #</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2 text-right">Grand Total</th>
                  <th className="px-3 py-2 text-right">Outstanding</th>
                  <th className="px-3 py-2 text-right">Apply</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {openInvs.map((inv) => {
                  const preview = fifoPreview?.find((x) => x.id === inv.id);
                  return (
                    <tr key={inv.id} data-testid={`rcp-advance-inv-${inv.id}`}>
                      <td className="px-3 py-2 font-mono text-xs font-semibold">{inv.invoice_no}</td>
                      <td className="px-3 py-2 text-xs">{formatDate(inv.invoice_date)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(inv.grand_total)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-700">{formatINR(inv.outstanding)}</td>
                      <td className="px-3 py-2 text-right">
                        {autoAllocate ? (
                          <span className="tabular-nums text-emerald-700 font-semibold text-sm" data-testid={`rcp-advance-fifo-${inv.id}`}>{preview ? formatINR(preview._apply) : "—"}</span>
                        ) : (
                          <Input type="number" step="0.01" min="0" max={inv.outstanding} value={manual[inv.id] || ""}
                            onChange={(e) => setManual((s) => ({ ...s, [inv.id]: e.target.value }))}
                            className="h-8 w-24 text-right tabular-nums font-mono ml-auto"
                            data-testid={`rcp-advance-manual-${inv.id}`} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 text-xs">
                <tr>
                  <td colSpan={4} className="px-3 py-2 text-right font-semibold">Total Applying</td>
                  <td className={`px-3 py-2 text-right font-semibold tabular-nums ${overApplied ? "text-red-600" : ""}`}>{formatINR(applyTotal)}</td>
                </tr>
                <tr>
                  <td colSpan={4} className="px-3 py-2 text-right font-semibold text-gray-500">Remaining Advance</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-amber-700">{formatINR(Math.max(0, available - applyTotal))}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex items-center gap-2">
          <div className="text-xs text-gray-500">
            {overApplied && <span className="text-red-600 font-semibold">⚠ Exceeds available advance.</span>}
          </div>
          <Button variant="outline" className="ml-auto" onClick={onClose} data-testid="rcp-advance-cancel">Close</Button>
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={save} disabled={saving || applyTotal <= 0.005 || overApplied || openInvs.length === 0} data-testid="rcp-advance-save">
            <Wallet className="h-4 w-4" /> {saving ? "Applying…" : `Apply ${formatINR(applyTotal)}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
